import ActionButton from '@/components/buttons/ActionButton.tsx';
import Chip from '@/components/ui/Chip.tsx';
import { InfoCard, InfoCardHeader } from '@/components/ui/InfoCard.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { pluralize } from '@/utils/pluralize.ts';
import { TourOptionOccupancyDto } from '@/features/dashboard/tours/types/dashboard/dto/TourOptionOccupancyDto.ts';
import { TourOptionParticipantDto } from '@/features/dashboard/tours/types/dashboard/dto/TourOptionParticipantDto.ts';
import OptionCrossTable from '@/features/dashboard/tours/components/options/OptionCrossTable.tsx';
import { filterResultNote } from '@/features/dashboard/tours/components/options/optionLabels.ts';
import {
  filterParticipants,
  isOptionFilterSelected,
  OptionFilter,
} from '@/features/dashboard/tours/components/options/optionFilters.ts';

interface OptionFilterCardProps {
  options: TourOptionOccupancyDto[];
  participants: TourOptionParticipantDto[];
  filters: OptionFilter[];
  onToggleFilter: (optionId: string, variantId: string) => void;
  onClearFilters: () => void;
}

/**
 * Zaznaczanie wariantów i tabela krzyżowa pod spodem. Jedna karta, bo pytanie
 * („kto ma kajaki i nocleg?”) i odpowiedź muszą stać obok siebie — filtr
 * przeliczany jest od razu, bez przycisku „Zastosuj”.
 */
const OptionFilterCard = ({
  options,
  participants,
  filters,
  onToggleFilter,
  onClearFilters,
}: OptionFilterCardProps) => {
  const matching = filterParticipants(participants, filters);

  return (
    <InfoCard>
      <InfoCardHeader
        title="Filtruj po opcjach"
        aside={
          <>
            <span className="text-[13px] text-neutral-500">
              zaznaczone warunki łączą się przez «i»
            </span>
            <ActionButton
              tone="outline"
              size="xs"
              className="ml-auto"
              disabled={filters.length === 0}
              onClick={onClearFilters}
            >
              Wyczyść filtry
            </ActionButton>
          </>
        }
      />

      <div className="flex flex-col gap-2.5 px-5 py-4">
        {options.map((option) => (
          <div
            key={option.optionId}
            className="flex flex-wrap items-center gap-2"
          >
            <span className="w-[186px] shrink-0 text-[13px] font-bold text-neutral-700">
              {option.name}
            </span>
            {option.variants.map((variant) => {
              const selected = isOptionFilterSelected(
                filters,
                option.optionId,
                variant.variantId,
              );

              return (
                // Pigułka jest `<span>`, więc warunek klika się przez opakowanie.
                <button
                  key={variant.variantId}
                  type="button"
                  aria-pressed={selected}
                  onClick={() =>
                    onToggleFilter(option.optionId, variant.variantId)
                  }
                  className="rounded-full"
                >
                  <Chip
                    label={variant.name}
                    count={variant.availability.currentCount}
                    tone={selected ? 'accent' : 'neutral'}
                    fill={selected ? 'soft' : 'outline'}
                    className="cursor-pointer"
                  />
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-baseline gap-x-2 border-y border-neutral-100 bg-neutral-50 px-5 py-2.5">
        <span className="text-[15px] font-bold tabular-nums text-ink">
          {pluralize(matching.length, 'osoba', 'osoby', 'osób')}
        </span>
        <span className="text-[13px] text-neutral-500">
          {filterResultNote(filters.length)}
        </span>
      </div>

      {matching.length === 0 ? (
        <div className="px-5 py-6">
          <NoItems
            title="Brak dopasowań"
            description="Żaden uczestnik nie spełnia wszystkich zaznaczonych warunków."
          />
        </div>
      ) : (
        <OptionCrossTable
          options={options}
          participants={matching}
          filters={filters}
        />
      )}
    </InfoCard>
  );
};

export default OptionFilterCard;
